import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Rating,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getProduct,
  getProductReviews,
} from "../../utility/httpActions/productActions";
import {
  likeReview,
  unlikeReview,
} from "../../utility/httpActions/reviewActions";
import { getPublicShop } from "../../utility/httpActions/shopActions";
import MessageShopButton from "../../common-components/MessageShopButton";
import TextInput from "../../common-components/TextInput";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import CategoryChip from "../../common-components/CategoryChip";
import { useForm } from "react-hook-form";
import {
  getUserCart,
  updateCartRequest,
} from "../../utility/httpActions/cartActions";
import useAuthentication from "../../hooks/useAuthentication";
import { trackEvent } from "../../utility/analytics";
import { resolveImageUri, shopCategories } from "../../utility/constants";
import { formatCityStateCountry } from "../../utility/addressUtils";
import { frontendRoutes } from "../../utility/frontendRoutes";
import { searchQuery } from "../../utility/httpActions/searchActions";
import {
  buildProductPath,
  buildShopPath,
} from "../../utility/utilityFunctions";
import { useEffect, useState } from "react";
import EntityErrorPage from "../../common-components/NotFoundPage";
import AddToCartModal from "../../common-components/Product/AddToCartModal";
import ConvertedPrice from "../../common-components/Product/ConvertedPrice";
import {
  formatProductPrice,
  formatCurrency,
  formatProductPriceLabel,
  getProductPriceBounds,
  isPriceHidden,
  isProductNegotiable,
  buildSearchFilterPath,
  getProductAvailableStock,
  isProductOutOfStock,
} from "../../utility/utilityFunctions";
import DeliveryInstructionsBox from "../../common-components/DeliveryInstructionsBox";

const LOW_STOCK_THRESHOLD = 5;

const DELIVERY_METHOD_LABELS = {
  PICKUP: "Pickup",
  DELIVERY: "Delivery",
  SERVICE: "Service",
  DIGITAL: "Digital",
};

const DELIVERY_METHOD_ICONS = {
  PICKUP: <StorefrontOutlinedIcon sx={{ fontSize: 15 }} />,
  DELIVERY: <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />,
  SERVICE: <WorkOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
  DIGITAL: <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />,
};

const PAYMENT_METHOD_LABELS = {
  CASH_ON_DELIVERY: "Cash on Delivery",
  BANK_TRANSFER: "Bank Transfer",
  VENMO: "Venmo",
  ZELLE: "Zelle",
};

const PAYMENT_METHOD_ICONS = {
  CASH_ON_DELIVERY: <MonetizationOnOutlinedIcon sx={{ fontSize: 15 }} />,
  BANK_TRANSFER: <AccountBalanceOutlinedIcon sx={{ fontSize: 15 }} />,
  VENMO: <MonetizationOnOutlinedIcon sx={{ fontSize: 15 }} />,
  ZELLE: <MonetizationOnOutlinedIcon sx={{ fontSize: 15 }} />,
};

const formatLabel = (val) =>
  (val || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getCategoryLabel = (value) => {
  const cat = shopCategories.find((c) => c.value === value);
  return cat ? `${cat.icon} ${cat.label}` : formatLabel(value);
};

const ratingColor = (r) => {
  if (!r) return "text.disabled";
  if (r >= 4.0) return "#16a34a";
  if (r >= 3.0) return "#d97706";
  return "#dc2626";
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      color: "text.disabled",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      display: "block",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const StatItem = ({ value, label, color }) => (
  <Box sx={{ textAlign: "center", mr: 3 }}>
    <Typography
      variant="h6"
      fontWeight={700}
      lineHeight={1.2}
      sx={{ color: color || "#1f2937" }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [addToCartNoteModalOpen, setAddToCartNoteModalOpen] = useState(false);
  const [addToCartError, setAddToCartError] = useState(null);
  const [quantityStockNote, setQuantityStockNote] = useState("");

  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => getProduct(productId),
  });

  // The route param is the slug when the page was opened from a slug link
  // (buildProductPath prefers it), and the cart stores product ObjectIds, so
  // always send the fetched product's real id.
  const addToCartPayload = {
    product: {
      id: product?.id || product?._id || productId,
      productName: product?.productName,
      price: product?.price,
      shop: product?.shop ? { ...product.shop } : undefined,
    },
    quantity: 1,
  };

  const { handleSubmit, control, getValues, setValue } = useForm({
    defaultValues: addToCartPayload,
    values: addToCartPayload,
  });

  const queryClient = useQueryClient();
  const { isLoggedIn, currentUser } = useAuthentication();

  // The product's populated shop is trimmed (no message number or contact
  // preferences), so the Message button needs the full shop. Same key as
  // ShopPage, so the two pages share the cache; isLoggedIn is in the key
  // because the number is only returned to logged-in viewers.
  const productShopId = product?.shop?.id || product?.shop?._id;
  const { data: messageShop } = useQuery({
    queryKey: ["publicShop", productShopId, isLoggedIn],
    queryFn: () => getPublicShop(productShopId),
    enabled: !!productShopId,
  });

  const { mutate: addToCart, isPending: isAddingToCart } = useMutation({
    mutationFn: (productInfo) => updateCartRequest(productInfo, isLoggedIn),
    onSuccess: (_, productInfo) => {
      queryClient.invalidateQueries({ queryKey: ["userCart"] });
      setAddToCartNoteModalOpen(false);
      setSnackbarMessage(
        productInfo?.offerPriceCents != null
          ? "Added to cart with your offer"
          : "Added to cart",
      );
      trackEvent("add_to_cart", {
        productId: productInfo?.product?.id,
        productName: productInfo?.product?.productName,
        price: productInfo?.product?.price,
        quantity: productInfo?.quantity,
        shopId:
          productInfo?.product?.shop?.id || productInfo?.product?.shop?._id,
        hasOffer: productInfo?.offerPriceCents != null,
      });
    },
    onError: (err) => {
      // e.g. an offer below the seller's hidden floor, which only the server knows.
      setAddToCartError(
        err?.data?.message || "Couldn't add this to your cart. Try again.",
      );
    },
  });

  // The product's existing cart line, so the modal can prefill its offer/note.
  const { data: cartData } = useQuery({
    queryKey: ["userCart", isLoggedIn],
    queryFn: () => getUserCart(isLoggedIn),
  });
  const realProductId = product?.id || product?._id;
  const existingCartItem = cartData?.cartItems?.find((item) => {
    const itemProductId = item?.product?.id || item?.product?._id || item?.product;
    return !!itemProductId && String(itemProductId) === String(realProductId);
  });

  const openAddToCartModal = () => {
    setAddToCartError(null);
    setAddToCartNoteModalOpen(true);
  };

  const handleAddToCart = ({ note, offerPriceCents }) => {
    setAddToCartError(null);
    handleSubmit((values) =>
      addToCart({
        ...values,
        // Clearing a prefilled note sends null so the cart line's note clears.
        note: note || (existingCartItem?.note ? null : undefined),
        offerPriceCents,
      }),
    )();
  };

  const { data: reviewsData } = useQuery({
    queryKey: ["productReviews", productId],
    queryFn: () => getProductReviews(productId, { limit: 5, sort: "newest" }),
    enabled: !!productId,
  });

  useEffect(() => {
    if (product) {
      trackEvent("product_viewed", {
        productId,
        shopId: product.shop?._id || product.shop?.id,
        source: "web",
      });
    }
  }, [productId, product]);

  const firstCategory = product?.categories?.[0];
  const { data: similarData } = useQuery({
    queryKey: ["similarProducts", productId, firstCategory],
    queryFn: () =>
      searchQuery({
        type: "products",
        categories: firstCategory,
        inStock: true,
        limit: 8,
      }),
    enabled: !!firstCategory,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !product) {
    return (
      <EntityErrorPage errorStatus={error?.status ?? 404} variant="product" />
    );
  }

  const images = product.images || [];
  const shop = product.shop;
  const shopId = shop?.id || shop?._id;

  const shopOwnerId =
    shop?.createdBy?._id?.toString() ?? shop?.createdBy?.toString() ?? null;
  const isOwnProduct =
    !!currentUser?.id && !!shopOwnerId && currentUser.id === shopOwnerId;
  const shopImageUri = shop?.profilePhoto
    ? resolveImageUri(shop.profilePhoto)
    : null;
  const currency = product.currency;
  const isOutOfStock = isProductOutOfStock(product);
  const availableStock = getProductAvailableStock(product);
  const isLowStock =
    product.showStockNudge !== false &&
    !isOutOfStock &&
    availableStock !== null &&
    availableStock > 0 &&
    availableStock <= LOW_STOCK_THRESHOLD;

  const numSold = product.numSold || 0;
  const numReviews = reviewsData?.total ?? product.numReviews ?? 0;
  const rating = product.rating || 0;

  const fulfillment = product.resolvedFulfillmentEstimate;
  const fulfillmentLabel =
    fulfillment?.minDays && fulfillment?.maxDays
      ? fulfillment.minDays === fulfillment.maxDays
        ? `Usually ready in ~${fulfillment.minDays} day${fulfillment.minDays === 1 ? "" : "s"}`
        : `Usually ready in ${fulfillment.minDays}–${fulfillment.maxDays} days`
      : null;

  const hasDeliveryOrPayment =
    product.deliveryMethods?.length > 0 || product.paymentMethods?.length > 0;

  const handleQuantityBlur = () => {
    const parsed = parseInt(getValues("quantity"), 10);
    if (isNaN(parsed) || parsed < 1) {
      setValue("quantity", "1");
      setQuantityStockNote("");
      return;
    }
    if (availableStock !== null && parsed > availableStock) {
      setValue("quantity", String(availableStock));
      setQuantityStockNote(
        "Quantity adjusted. That's more than what's available.",
      );
    } else {
      setQuantityStockNote("");
    }
  };

  const handleShareProduct = async () => {
    const shareLink = product.slug
      ? `${window.location.origin}/product/${product.slug}`
      : `${window.location.origin}/product/${productId}`;
    const shareData = {
      title: `${product.productName} on Ngwaahia`,
      url: shareLink,
    };
    trackEvent("product_shared", {
      productId,
      productName: product.productName,
    });
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareLink);
      setSnackbarMessage("Link copied to clipboard");
    }
  };

  // Note: this only updates the DOM after JS runs, so it fixes the browser
  // tab title and helps a JS-executing crawler (modern Googlebot) — it does
  // NOT produce link-preview cards on WhatsApp/Twitter/Facebook when a
  // product URL is shared, since those scrapers don't execute JS. That needs
  // server-side meta injection in server.js (see dev-docs SEO plan).
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
      <Helmet>
        <title>{product.productName} | Ngwaahia</title>
        <meta
          name="description"
          content={
            product.description
              ? product.description.slice(0, 160)
              : `${product.productName} on Ngwaahia — buy directly from the seller.`
          }
        />
      </Helmet>
      {/* Report warning banner */}
      {product.hasReportWarning && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            bgcolor: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: 2,
            p: 1.5,
            mb: 3,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{ color: "#c2410c", fontSize: 18, mt: 0.1, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            sx={{ color: "#9a3412", lineHeight: 1.6 }}
          >
            Multiple users have reported issues with this product. Proceed with
            caution and verify before purchasing.
          </Typography>
        </Box>
      )}

      <Grid container spacing={4}>
        {/* Image gallery */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: "100%",
              aspectRatio: "1 / 1",
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {images.length > 0 ? (
              <ImageWithLoader
                src={resolveImageUri(images[activeImage])}
                alt={product.productName}
                objectFit="contain"
              />
            ) : (
              <Typography color="text.secondary">No images</Typography>
            )}
          </Box>
          {images.length > 1 && (
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              {images.map((img, i) => (
                <Box
                  key={img.path || i}
                  onClick={() => setActiveImage(i)}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    border:
                      i === activeImage
                        ? "2px solid #046287"
                        : "2px solid transparent",
                    flexShrink: 0,
                  }}
                >
                  <ImageWithLoader src={resolveImageUri(img)} alt="" />
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Product info */}
        <Grid item xs={12} md={6}>
          {/* Categories */}
          {product.categories?.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
              {product.categories.map((c) => (
                <CategoryChip
                  key={c}
                  label={getCategoryLabel(c)}
                  onClick={() =>
                    navigate(buildSearchFilterPath({ category: c }))
                  }
                />
              ))}
            </Box>
          )}

          {/* Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {product.productName}
            </Typography>
            <Box sx={{ display: "flex", flexShrink: 0 }}>
              <MessageShopButton
                shop={messageShop}
                source="product_page"
                message={`Hello ${shop?.shopName || "there"}, I'm interested in your product: ${product.productName}\n${window.location.origin}${buildProductPath(product)}`}
              />
              <Tooltip title="Share product">
                <IconButton
                  size="small"
                  onClick={handleShareProduct}
                  sx={{ color: "#046287" }}
                >
                  <IosShareOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Price + stock badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h4" fontWeight={700} color="#046287">
              {formatProductPriceLabel({ ...product, currency })}
            </Typography>
            {/* A hidden price's label already says "Open to offers". */}
            {isProductNegotiable(product) && !isPriceHidden(product) && (
              <Chip
                label="Open to offers"
                color="success"
                variant="outlined"
                size="small"
              />
            )}
            {isOutOfStock && (
              <Chip label="Out of stock" color="error" size="small" />
            )}
            {isLowStock && (
              <Chip
                label={`Only ${availableStock} left`}
                color="warning"
                size="small"
              />
            )}
          </Box>
          <ConvertedPrice
            {...getProductPriceBounds(product)}
            currency={currency}
            variant="body2"
            sx={{ mt: -1 }}
            showExplainer
          />
          {/* Only sent for hidden price listings with a few recent sales. */}
          {isPriceHidden(product) && product.recentSoldPriceRange && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Recently sold for{" "}
              {product.recentSoldPriceRange.minCents ===
              product.recentSoldPriceRange.maxCents
                ? formatCurrency(
                    product.recentSoldPriceRange.minCents / 100,
                    currency,
                  )
                : `${formatCurrency(
                    product.recentSoldPriceRange.minCents / 100,
                    currency,
                  )} – ${formatCurrency(
                    product.recentSoldPriceRange.maxCents / 100,
                    currency,
                  )}`}
            </Typography>
          )}

          {/* Stats row */}
          <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2 }}>
            <StatItem value={numSold} label="Sold" />
            {numReviews > 0 ? (
              <Box
                component={Link}
                to={generatePath(frontendRoutes.PRODUCT_REVIEWS, { productId })}
                sx={{ textDecoration: "none" }}
              >
                <StatItem
                  value={rating ? Number(rating).toFixed(1) : "—"}
                  label="Rating"
                  color={ratingColor(rating)}
                />
              </Box>
            ) : (
              <StatItem value="—" label="Rating" color={ratingColor(0)} />
            )}
            {numReviews > 0 ? (
              <Box
                component={Link}
                to={generatePath(frontendRoutes.PRODUCT_REVIEWS, { productId })}
                sx={{ textDecoration: "none" }}
              >
                <StatItem
                  value={numReviews}
                  label={numReviews === 1 ? "Review" : "Reviews"}
                />
              </Box>
            ) : (
              <StatItem value={0} label="Reviews" />
            )}
          </Box>

          {/* Description */}
          {product.description && (
            <Box sx={{ mb: 2 }}>
              <SectionLabel>About this product</SectionLabel>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                  lineHeight: 1.7,
                }}
              >
                {product.description}
              </Typography>
            </Box>
          )}

          {/* Order instructions */}
          {product.resolvedOrderInstructions && (
            <Box sx={{ mb: 2 }}>
              <SectionLabel>Order instructions</SectionLabel>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                  lineHeight: 1.7,
                }}
              >
                {product.resolvedOrderInstructions}
              </Typography>
            </Box>
          )}

          {/* How to get it — delivery + payment + fulfillment */}
          {hasDeliveryOrPayment && (
            <Box sx={{ mb: 2, pt: 1.5, borderTop: "1px solid #f3f4f6" }}>
              <SectionLabel>How to get it</SectionLabel>

              {product.deliveryMethods?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: product.paymentMethods?.length > 0 ? 1.5 : 0,
                  }}
                >
                  {product.deliveryMethods.map((method) => (
                    <Box
                      key={method}
                      onClick={() =>
                        navigate(
                          buildSearchFilterPath({
                            deliveryMethods: [method],
                            categories: product.categories,
                          }),
                        )
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: "#046287",
                        cursor: "pointer",
                      }}
                    >
                      {DELIVERY_METHOD_ICONS[method]}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#046287"
                      >
                        {DELIVERY_METHOD_LABELS[method] || formatLabel(method)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {product.deliveryMethods?.includes("DELIVERY") && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: product.paymentMethods?.length > 0 ? 1.5 : 0 }}
                >
                  Delivery fee:{" "}
                  {formatProductPrice(product.deliveryFee, currency)}
                </Typography>
              )}

              <DeliveryInstructionsBox
                instructions={product.resolvedDeliveryInstructions}
                sx={{ mb: product.paymentMethods?.length > 0 ? 1.5 : 0 }}
              />

              {product.paymentMethods?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: fulfillmentLabel ? 1.5 : 0,
                  }}
                >
                  {product.paymentMethods.map((method) => (
                    <Box
                      key={method}
                      onClick={() =>
                        navigate(
                          buildSearchFilterPath({
                            paymentMethods: [method],
                            categories: product.categories,
                          }),
                        )
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: "#046287",
                        cursor: "pointer",
                      }}
                    >
                      {PAYMENT_METHOD_ICONS[method]}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#046287"
                      >
                        {PAYMENT_METHOD_LABELS[method] || formatLabel(method)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {fulfillmentLabel && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    mt: 0.5,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#0369a1" }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "#0369a1", fontWeight: 500, fontSize: 12 }}
                  >
                    {fulfillmentLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Add to cart */}
          {isOwnProduct ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mt: 2, mb: 3 }}
            >
              This is your product — you can't order from your own shop.
            </Typography>
          ) : (
            !isOutOfStock && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 2,
                  mb: 3,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InputLabel
                      htmlFor="quantity-input"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Quantity
                    </InputLabel>
                    <TextInput
                      control={control}
                      id="quantity-input"
                      name="quantity"
                      type="number"
                      sx={{ width: 80 }}
                      inputProps={
                        availableStock !== null
                          ? { min: 1, max: availableStock }
                          : { min: 1 }
                      }
                      onBlur={handleQuantityBlur}
                      rules={{
                        validate: (value) =>
                          parseInt(value) > 0 ||
                          "Enter a quantity greater than 0",
                      }}
                    />
                  </Box>
                  {quantityStockNote && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#b45309", fontSize: 12, mt: 0.5 }}
                    >
                      {quantityStockNote}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={openAddToCartModal}
                  disabled={isAddingToCart}
                  sx={{
                    bgcolor: "#046287",
                    "&:hover": { bgcolor: "#035070" },
                    borderRadius: 2,
                    px: 4,
                    py: 1.25,
                    fontSize: "0.95rem",
                  }}
                >
                  {isAddingToCart
                    ? "Adding…"
                    : isPriceHidden(product)
                      ? "Make an offer"
                      : "Add to cart"}
                </Button>
              </Box>
            )
          )}

          {/* Shop card */}
          {shop && shopId && (
            <Box
              component={Link}
              to={buildShopPath(shop)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 2,
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                textDecoration: "none",
                color: "inherit",
                "&:hover": { borderColor: "#046287" },
                transition: "border-color 0.15s",
              }}
            >
              {shopImageUri ? (
                <ImageWithLoader
                  src={shopImageUri}
                  alt={shop.shopName}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "#046287",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {(shop.shopName || "S")[0].toUpperCase()}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Sold by
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {shop.shopName}
                  </Box>
                  {shop.isIdentityVerified && <VerifiedBadge size={15} />}
                </Typography>
                {formatCityStateCountry(shop.address) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <LocationOnOutlinedIcon sx={{ fontSize: 14 }} />
                    <Box
                      component="span"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {formatCityStateCountry(shop.address)}
                    </Box>
                  </Typography>
                )}
                {shop.numReviews > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    <Rating
                      value={shop.rating || 0}
                      precision={0.1}
                      readOnly
                      size="small"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={12}
                    >
                      ({shop.numReviews})
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="body2"
                color="#046287"
                fontWeight={600}
                sx={{ ml: "auto", flexShrink: 0 }}
              >
                Visit shop →
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Reviews */}
      <ReviewsSection
        reviews={reviewsData?.items}
        total={numReviews}
        rating={rating}
        productId={productId}
      />

      {/* Similar products */}
      <SimilarProducts
        products={similarData?.products?.results}
        currentProductIds={[
          productId,
          product?.id,
          product?._id,
          product?.slug,
        ]}
      />

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AddToCartModal
        open={addToCartNoteModalOpen}
        onClose={() => setAddToCartNoteModalOpen(false)}
        onSubmit={handleAddToCart}
        isSubmitting={isAddingToCart}
        orderInstructions={product?.resolvedOrderInstructions}
        deliveryInstructions={product?.resolvedDeliveryInstructions}
        product={product}
        quantity={getValues("quantity")}
        initialNote={existingCartItem?.note}
        initialOfferPriceCents={existingCartItem?.offerPriceCents ?? null}
        submitError={addToCartError}
      />
    </Box>
  );
};

/* ── Sub-components ── */

const LIKE_ACTIVE_COLOR = "#046287";
const LIKE_INACTIVE_COLOR = "#4B5563";

const ReviewLikeButton = ({
  reviewId,
  likesCount = 0,
  isLikedByUser = false,
}) => {
  const { isLoggedIn } = useAuthentication();
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState({
    liked: isLikedByUser,
    count: likesCount,
  });

  const { mutate } = useMutation({
    mutationFn: (currentlyLiked) =>
      currentlyLiked ? unlikeReview(reviewId) : likeReview(reviewId),
    onMutate: (currentlyLiked) => {
      setOptimistic({
        liked: !currentlyLiked,
        count: currentlyLiked ? optimistic.count - 1 : optimistic.count + 1,
      });
    },
    onError: () => setOptimistic({ liked: isLikedByUser, count: likesCount }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["productReviews"] }),
  });

  const color = optimistic.liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR;

  return (
    <Box
      onClick={() => {
        if (isLoggedIn) mutate(optimistic.liked);
      }}
      title={
        isLoggedIn ? (optimistic.liked ? "Unlike" : "Like") : "Sign in to like"
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        cursor: isLoggedIn ? "pointer" : "default",
        py: "2px",
        userSelect: "none",
        width: "fit-content",
      }}
    >
      {optimistic.liked ? (
        <ThumbUpIcon sx={{ fontSize: 17, color }} />
      ) : (
        <ThumbUpOutlinedIcon sx={{ fontSize: 17, color }} />
      )}
      {optimistic.count > 0 && (
        <Typography
          sx={{ fontSize: 13, color, fontWeight: 500, lineHeight: 1 }}
        >
          {optimistic.count}
        </Typography>
      )}
    </Box>
  );
};

const ReviewCard = ({ review, productId }) => {
  const productRating = review.productRatings?.find(
    (pr) => (pr.product?.id || pr.product) === productId,
  );
  const rating = productRating?.rating ?? review.shopRating;
  const text = productRating?.text || review.shopText;
  const userName = review.user?.name || "Buyer";
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
  const photos =
    review.media?.filter((m) => m.type === "image").map((m) => m.url) ?? [];

  return (
    <Box
      sx={{
        py: 2.5,
        borderBottom: "1px solid #f3f4f6",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Avatar
          src={
            review.user?.profilePhoto
              ? resolveImageUri(review.user.profilePhoto)
              : undefined
          }
          sx={{ width: 36, height: 36, bgcolor: "#046287", fontSize: 14 }}
        >
          {userName[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {userName}
            {review.user?.isIdentityVerified && <VerifiedBadge size={13} />}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize={12}>
            {date}
          </Typography>
        </Box>
        <Rating value={rating} precision={0.5} readOnly size="small" />
      </Box>

      {review.tags?.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
          {review.tags.map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}

      {text && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {text}
        </Typography>
      )}

      {photos.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          {photos.slice(0, 5).map((url, i) => (
            <Box
              key={i}
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "#f5f5f5",
              }}
            >
              <ImageWithLoader src={url} alt="" />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1, ml: 1 }}>
        <ReviewLikeButton
          reviewId={review._id || review.id}
          likesCount={review.likesCount ?? 0}
          isLikedByUser={review.isLikedByUser ?? false}
        />
      </Box>
    </Box>
  );
};

const ReviewsSection = ({ reviews, total, rating, productId }) => {
  if (!reviews) return null;
  const allReviewsPath = generatePath(frontendRoutes.PRODUCT_REVIEWS, {
    productId,
  });

  return (
    <Box sx={{ mt: 6 }}>
      <Divider sx={{ mb: 4 }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Reviews
          </Typography>
          {total > 0 && (
            <>
              <Rating value={rating} precision={0.1} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">
                {Number(rating).toFixed(1)} · {total}{" "}
                {total === 1 ? "review" : "reviews"}
              </Typography>
            </>
          )}
        </Box>
        {total > 5 && (
          <Typography
            component={Link}
            to={allReviewsPath}
            variant="body2"
            sx={{
              color: "#046287",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            See all {total} →
          </Typography>
        )}
      </Box>
      {reviews.length === 0 ? (
        <Typography color="text.secondary" fontStyle="italic">
          No reviews yet — be the first after you order!
        </Typography>
      ) : (
        <>
          <Box>
            {reviews.map((review) => (
              <ReviewCard
                key={review._id || review.id}
                review={review}
                productId={productId}
              />
            ))}
          </Box>
          {total > reviews.length && (
            <Box sx={{ mt: 2 }}>
              <Typography
                component={Link}
                to={allReviewsPath}
                variant="body2"
                sx={{
                  display: "block",
                  textAlign: "center",
                  py: 1.5,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  color: "#046287",
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": { borderColor: "#046287", bgcolor: "#f0f9ff" },
                }}
              >
                See all {total} reviews
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// The URL param can be a slug or an id, so exclude the current product by
// every identifier it has, not just the param.
const SimilarProducts = ({ products, currentProductIds }) => {
  const currentIds = new Set(currentProductIds.filter(Boolean).map(String));
  const filtered = products
    ?.filter(
      (p) =>
        ![p.id, p._id, p.slug]
          .filter(Boolean)
          .some((id) => currentIds.has(String(id))),
    )
    .slice(0, 6);
  if (!filtered?.length) return null;

  return (
    <Box sx={{ mt: 6 }}>
      <Divider sx={{ mb: 4 }} />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
        Similar products
      </Typography>
      <Grid container spacing={2}>
        {filtered.map((product) => {
          const pid = product.id || product._id;
          const imageUri = product.images?.[0]
            ? resolveImageUri(product.images[0])
            : null;
          const isOos = product.status === "OUT_OF_STOCK";
          return (
            <Grid item xs={6} sm={4} md={2} key={pid}>
              <Box
                component={Link}
                to={buildProductPath(product)}
                sx={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  overflow: "hidden",
                  "&:hover": { borderColor: "#046287" },
                  transition: "border-color 0.15s",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1/1",
                    bgcolor: "#f5f5f5",
                    position: "relative",
                  }}
                >
                  {imageUri ? (
                    <ImageWithLoader src={imageUri} alt={product.productName} />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary" fontSize={11}>
                        No image
                      </Typography>
                    </Box>
                  )}
                  {isOos && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{ color: "white", fontSize: 11, fontWeight: 600 }}
                      >
                        Out of stock
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                    }}
                  >
                    {product.productName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#046287"
                    fontWeight={700}
                    fontSize={12}
                  >
                    {formatProductPriceLabel(product)}
                  </Typography>
                  {product.numReviews > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      <Rating
                        value={product.rating || 0}
                        precision={0.1}
                        readOnly
                        size="small"
                        sx={{ fontSize: 10 }}
                      />
                      <Typography fontSize={10} color="text.secondary">
                        ({product.numReviews})
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ProductDetails;
